import PostHog from 'posthog-react-native';
import { APP_CONFIG } from '@/lib/app/config';
import { redactAnalyticsURL } from '@/lib/analytics/urlPrivacy';
import { deriveSafeSearchAnalytics } from '@/lib/analytics/searchAnalyticsPrivacy';
import {
  ANALYTICS_QUEUE_MAX_EVENTS,
  AnalyticsQueueStorage,
  boundAnalyticsQueueState,
} from '@/lib/analytics/analyticsQueue';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

type AnalyticsPrimitive = string | number | boolean | null | undefined;
type AnalyticsProps = Record<string, AnalyticsPrimitive>;
type CapturePrimitive = Exclude<AnalyticsPrimitive, undefined>;
type CaptureProps = Record<string, CapturePrimitive>;

type EmptyStateReason = 'no_events_nearby' | 'no_results' | 'error';
type EventLinkType = 'tickets' | 'website' | 'directions';
type EventShareMethod = 'native_share' | 'copy_link';
type LoadScreen = 'events' | 'event_detail' | 'map' | 'profile' | 'search' | 'pulse';
type LaunchEventName =
  | 'create_event_open'
  | 'edit_event_open'
  | 'flyer_draft_cleared'
  | 'flyer_duplicate_detected'
  | 'flyer_import_failed'
  | 'flyer_import_started'
  | 'flyer_import_succeeded'
  | 'flyer_manual_override';
type MapFilterType =
  | 'time_window'
  | 'radius'
  | 'category'
  | 'entry_type'
  | 'mode'
  | 'event_map_mode'
  | 'scene'
  | 'venue_activity'
  | 'venue_deals'
  | 'venue_deal_categories'
  | 'reset';
type OnboardingStep = 'welcome' | 'location' | 'outside_sa' | 'notifications' | 'privacy';
type PermissionPromptSource =
  | 'location_onboarding'
  | 'notification_onboarding'
  | 'notification_settings';
type ProfileField =
  | 'username'
  | 'display_name'
  | 'bio'
  | 'avatar_url'
  | 'default_rsvp_public'
  | 'interest_tags';
type UserAction =
  | 'unknown'
  | 'load_feed'
  | 'load_event'
  | 'load_search'
  | 'load_profile'
  | 'submit_rsvp';
type VenueProfileSource = 'map_sheet' | 'profile' | 'event_detail' | 'search';
type VenueClaimSource = 'venue_page' | 'event_detail';
type VenueDirectionsSurface = 'venue_page' | 'map_sheet' | 'web_map' | 'web_venue_page';
type VenueLinkSurface = 'venue_page' | 'map_sheet' | 'web_venue_page';
type AnalyticsSource =
  | 'event_detail'
  | 'feed'
  | 'map'
  | 'profile'
  | 'push'
  | 'search'
  | 'share_link'
  | 'unknown'
  | 'venue_page';
type AnalyticsSurface =
  | 'web'
  | 'landing_site'
  | 'mobile'
  | 'mobile_business'
  | 'mobile_business_inbox'
  | 'mobile_event_detail'
  | 'mobile_feed'
  | 'mobile_map_sheet'
  | 'mobile_search'
  | 'mobile_venue_page';
type VenueProfileIdentity = {
  profileUserID?: string | null;
  profileUsername?: string | null;
  surface?: AnalyticsSurface;
};
type FeatureFlagTraits = {
  appVersion?: string | null;
  hasBusinessContext?: boolean;
  hasClaimedVenue?: boolean;
  isStaff?: boolean;
  orgMembershipCount?: number;
  platform?: string | null;
};
type WorkspaceContextKind = 'business' | 'personal';
type BusinessAction =
  | 'cancel_event'
  | 'create_event'
  | 'delete_event'
  | 'edit_event'
  | 'edit_venue'
  | 'event_operations'
  | 'view_event'
  | 'view_live_page';
type BusinessNotificationAction = 'dismiss' | 'filter_all_venues' | 'filter_venue' | 'open';
type BusinessDealAction =
  | 'activate'
  | 'archive'
  | 'created'
  | 'open_create'
  | 'open_edit'
  | 'pause'
  | 'restore_date'
  | 'updated';

const VIEW_DURATION_THRESHOLD_SECONDS = 3;
const ANALYTICS_EVENTS_PATH = '/analytics/events';
const ANALYTICS_EVENTS_BATCH_PATH = '/analytics/events/batch';
const ANALYTICS_BATCH_FLUSH_MS = 1500;
const ANALYTICS_BATCH_MAX_SIZE = 25;
const ANALYTICS_MAX_ATTEMPTS = 3;
const ANALYTICS_NETWORK_RETRY_MS = 30_000;
const WEB_ANALYTICS_ANON_ID_KEY = 'radious_web_posthog_anon_id';
const WEB_ANALYTICS_IDENTIFIED_ID_KEY = 'radious_web_posthog_identified_id';
const NATIVE_ANALYTICS_ANON_ID_KEY = 'radious:analytics:anonymous_id';

const searchQueryKeys = new Set<string>();

let client: PostHog | null = null;
let initialized = false;
let analyticsMirrorAccessToken: string | null = null;
let analyticsIdentifiedUserID: string | null = null;
let lastWebPageViewKey: string | null = null;
let analyticsBatchFlushTimer: ReturnType<typeof setTimeout> | null = null;
let analyticsAnonymousID = createAnalyticsID();
let nativeAnalyticsIdentityPromise: Promise<void> | null = null;
let analyticsSessionID = createAnalyticsID();
const analyticsBatchQueue: AnalyticsMirrorEvent[] = [];
const analyticsBatchRetryCounts = new Map<string, number>();
const analyticsQuarantine: AnalyticsMirrorEvent[] = [];
let analyticsSingleEventFlushes = 0;
let analyticsQueueRestorePromise: Promise<void> | null = null;
let analyticsCaptureChain = Promise.resolve();
let analyticsMirrorFlushPromise: Promise<void> | null = null;

type AnalyticsMirrorEvent = {
  captured_user_id: string;
  name: string;
  anonymous_id: string;
  deal_id: string;
  event_id: string;
  fixture_id: string;
  idempotency_key: string;
  platform: string;
  path: string;
  session_id: string;
  source: string;
  surface: string;
  venue_id: string;
  metadata_json: string;
  occurred_at: string;
};

const analyticsQueueStorage = new AnalyticsQueueStorage<AnalyticsMirrorEvent>(AsyncStorage);

function getClient() {
  if (!APP_CONFIG.posthogKey || Platform.OS === 'web') {
    return null;
  }

  if (!client) {
    client = new PostHog(APP_CONFIG.posthogKey, {
      host: APP_CONFIG.posthogHost,
      captureAppLifecycleEvents: false,
    });
  }

  return client;
}

function normalizeProps(properties: AnalyticsProps): CaptureProps {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined)
  ) as CaptureProps;
}

function posthogCaptureURL() {
  return `${APP_CONFIG.posthogHost.replace(/\/+$/, '')}/capture/`;
}

function getWebStorage() {
  if (Platform.OS !== 'web') {
    return null;
  }

  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function createAnalyticsID() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getWebDistinctID() {
  const storage = getWebStorage();
  if (!storage) {
    return null;
  }

  const identifiedID = storage.getItem(WEB_ANALYTICS_IDENTIFIED_ID_KEY);
  if (identifiedID) {
    return identifiedID;
  }

  const existingAnonID = storage.getItem(WEB_ANALYTICS_ANON_ID_KEY);
  if (existingAnonID) {
    return existingAnonID;
  }

  const nextAnonID = createAnalyticsID();
  storage.setItem(WEB_ANALYTICS_ANON_ID_KEY, nextAnonID);
  return nextAnonID;
}

function ensureNativeAnalyticsIdentity() {
  if (Platform.OS === 'web') {
    return null;
  }

  if (!nativeAnalyticsIdentityPromise) {
    const posthog = getClient();
    const distinctID = posthog
      ? posthog.ready().then(() => posthog.getDistinctId())
      : AsyncStorage.getItem(NATIVE_ANALYTICS_ANON_ID_KEY);
    nativeAnalyticsIdentityPromise = distinctID
      .then(async (distinctID) => {
        analyticsAnonymousID = distinctID || analyticsAnonymousID;
        await AsyncStorage.setItem(NATIVE_ANALYTICS_ANON_ID_KEY, analyticsAnonymousID);
      })
      .catch(() => undefined);
  }

  return nativeAnalyticsIdentityPromise;
}

function sendWebPayload(payload: Record<string, unknown>) {
  if (Platform.OS !== 'web' || !APP_CONFIG.posthogKey) {
    return;
  }

  void fetch(posthogCaptureURL(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => undefined);
}

function analyticsMirrorPath() {
  if (Platform.OS === 'web') {
    return globalThis.location?.pathname ?? '';
  }
  return '';
}

function analyticsMirrorAnonymousID() {
  if (Platform.OS === 'web') {
    return getWebDistinctID() ?? '';
  }
  return analyticsAnonymousID;
}

function analyticsMirrorSurface(properties: CaptureProps) {
  const surface = stringProp(properties.surface);
  if (surface) {
    return surface;
  }
  return Platform.OS === 'web' ? 'web' : 'mobile';
}

function mirrorCapture(name: string, properties: CaptureProps) {
  const idempotencyKey = stringProp(properties.idempotency_key) || createAnalyticsID();
  const occurredAt = new Date().toISOString();
  analyticsCaptureChain = analyticsCaptureChain
    .catch(() => undefined)
    .then(async () => {
      await ensureNativeAnalyticsIdentity();
      await restoreAnalyticsMirrorQueue();
      await queueAnalyticsMirrorEvent({
        name,
        captured_user_id: analyticsIdentifiedUserID ?? '',
        anonymous_id: analyticsMirrorAnonymousID(),
        deal_id: stringProp(properties.deal_id),
        event_id: stringProp(properties.event_id),
        fixture_id: stringProp(properties.fixture_id),
        idempotency_key: idempotencyKey,
        platform: Platform.OS,
        path: analyticsMirrorPath(),
        session_id: stringProp(properties.session_id) || analyticsSessionID,
        source: stringProp(properties.source),
        surface: analyticsMirrorSurface(properties),
        venue_id: stringProp(properties.venue_id),
        metadata_json: JSON.stringify(properties),
        occurred_at: occurredAt,
      });
    });
}

function analyticsMirrorHeaders(events: AnalyticsMirrorEvent[]) {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });
  const capturedUserID = events[0]?.captured_user_id;
  const canUseCurrentSession =
    capturedUserID &&
    capturedUserID === analyticsIdentifiedUserID &&
    events.every((event) => event.captured_user_id === capturedUserID);
  if (analyticsMirrorAccessToken && canUseCurrentSession) {
    headers.set('Authorization', `Bearer ${analyticsMirrorAccessToken}`);
  }
  return headers;
}

function analyticsTransportEvent(event: AnalyticsMirrorEvent) {
  const { captured_user_id: _capturedUserID, ...transportEvent } = event;
  return transportEvent;
}

function persistedAnalyticsQueueState() {
  return {
    pending: analyticsBatchQueue.map((event) => ({
      event,
      attempts: analyticsBatchRetryCounts.get(event.idempotency_key) ?? 1,
    })),
    quarantine: [...analyticsQuarantine],
    singleEventFlushes: analyticsSingleEventFlushes,
  };
}

function persistAnalyticsMirrorQueue() {
  const bounded = boundAnalyticsQueueState(persistedAnalyticsQueueState());
  analyticsBatchQueue.splice(
    0,
    analyticsBatchQueue.length,
    ...bounded.pending.map(({ event }) => event)
  );
  analyticsBatchRetryCounts.clear();
  for (const { event, attempts } of bounded.pending) {
    analyticsBatchRetryCounts.set(event.idempotency_key, attempts);
  }
  analyticsQuarantine.splice(0, analyticsQuarantine.length, ...bounded.quarantine);
  analyticsSingleEventFlushes = bounded.singleEventFlushes;
  return analyticsQueueStorage.save(bounded);
}

function restoreAnalyticsMirrorQueue() {
  if (!analyticsQueueRestorePromise) {
    analyticsQueueRestorePromise = analyticsQueueStorage.restore().then((restored) => {
      const existingKeys = new Set(analyticsBatchQueue.map((event) => event.idempotency_key));
      for (const { event, attempts } of [...restored.pending].reverse()) {
        if (existingKeys.has(event.idempotency_key)) continue;
        analyticsBatchQueue.unshift(event);
        analyticsBatchRetryCounts.set(event.idempotency_key, attempts);
        existingKeys.add(event.idempotency_key);
      }
      analyticsQuarantine.push(...restored.quarantine);
      analyticsSingleEventFlushes = Math.max(
        analyticsSingleEventFlushes,
        restored.singleEventFlushes
      );
    });
  }
  return analyticsQueueRestorePromise;
}

async function queueAnalyticsMirrorEvent(event: AnalyticsMirrorEvent) {
  if (!APP_CONFIG.apiBaseURL) {
    return;
  }

  analyticsBatchQueue.push(event);
  if (analyticsBatchQueue.length > ANALYTICS_QUEUE_MAX_EVENTS) {
    const evicted = analyticsBatchQueue.shift();
    if (evicted) analyticsBatchRetryCounts.delete(evicted.idempotency_key);
  }
  await persistAnalyticsMirrorQueue();
  if (analyticsBatchQueue.length >= ANALYTICS_BATCH_MAX_SIZE) {
    void flushAnalyticsMirror();
    return;
  }
  scheduleAnalyticsMirrorFlush();
}

function scheduleAnalyticsMirrorFlush(delayMs = ANALYTICS_BATCH_FLUSH_MS) {
  if (analyticsBatchFlushTimer) {
    return;
  }
  analyticsBatchFlushTimer = setTimeout(() => {
    analyticsBatchFlushTimer = null;
    void flushAnalyticsMirror();
  }, delayMs);
}

function flushAnalyticsMirror() {
  if (!analyticsMirrorFlushPromise) {
    analyticsMirrorFlushPromise = performAnalyticsMirrorFlush().finally(() => {
      analyticsMirrorFlushPromise = null;
    });
  }
  return analyticsMirrorFlushPromise;
}

async function performAnalyticsMirrorFlush() {
  await analyticsCaptureChain;
  await restoreAnalyticsMirrorQueue();
  if (!APP_CONFIG.apiBaseURL || analyticsBatchQueue.length === 0) {
    return;
  }
  await ensureNativeAnalyticsIdentity();

  if (analyticsBatchFlushTimer) {
    clearTimeout(analyticsBatchFlushTimer);
    analyticsBatchFlushTimer = null;
  }

  const batchSize = analyticsSingleEventFlushes > 0 ? 1 : ANALYTICS_BATCH_MAX_SIZE;
  const capturedUserID = analyticsBatchQueue[0]?.captured_user_id ?? '';
  const compatibleBatchSize = analyticsBatchQueue
    .slice(0, batchSize)
    .findIndex((event) => (event.captured_user_id ?? '') !== capturedUserID);
  const events = analyticsBatchQueue.splice(
    0,
    compatibleBatchSize === -1 ? batchSize : compatibleBatchSize
  );
  if (analyticsSingleEventFlushes > 0) analyticsSingleEventFlushes--;
  const endpoint = events.length === 1 ? ANALYTICS_EVENTS_PATH : ANALYTICS_EVENTS_BATCH_PATH;
  const transportEvents = events.map(analyticsTransportEvent);
  const body =
    transportEvents.length === 1
      ? JSON.stringify(transportEvents[0])
      : JSON.stringify({ events: transportEvents });

  let response: Response | undefined;
  try {
    response = await fetch(`${APP_CONFIG.apiBaseURL}${endpoint}`, {
      method: 'POST',
      headers: analyticsMirrorHeaders(events),
      body,
      keepalive: Platform.OS === 'web',
    });
  } catch {
    analyticsBatchQueue.unshift(...events);
    await persistAnalyticsMirrorQueue();
    scheduleAnalyticsMirrorFlush(ANALYTICS_NETWORK_RETRY_MS);
    return;
  }

  if (!response.ok) {
    if (
      response.status >= 400 &&
      response.status < 500 &&
      response.status !== 429 &&
      events.length > 1
    ) {
      analyticsBatchQueue.unshift(...events);
      analyticsSingleEventFlushes += events.length;
      await persistAnalyticsMirrorQueue();
      scheduleAnalyticsMirrorFlush();
      return;
    }
    if (response.status === 401 || response.status === 403) {
      analyticsBatchQueue.unshift(...events);
      await persistAnalyticsMirrorQueue();
      return;
    }
    await retryAnalyticsMirrorEvents(events);
    return;
  }

  for (const event of events) analyticsBatchRetryCounts.delete(event.idempotency_key);
  await persistAnalyticsMirrorQueue();

  if (analyticsBatchQueue.length > 0) {
    scheduleAnalyticsMirrorFlush();
  }
}

async function retryAnalyticsMirrorEvents(events: AnalyticsMirrorEvent[]) {
  const retryable = events.filter((event) => {
    const attempts = (analyticsBatchRetryCounts.get(event.idempotency_key) ?? 1) + 1;
    analyticsBatchRetryCounts.set(event.idempotency_key, attempts);
    if (attempts <= ANALYTICS_MAX_ATTEMPTS) return true;
    if (analyticsQuarantine.length >= ANALYTICS_BATCH_MAX_SIZE) analyticsQuarantine.shift();
    analyticsQuarantine.push(event);
    console.warn('Analytics event quarantined after repeated delivery failures.', {
      event: event.name,
      idempotency_key: event.idempotency_key,
    });
    return false;
  });
  if (retryable.length > 0) {
    analyticsBatchQueue.unshift(...retryable);
  }
  await persistAnalyticsMirrorQueue();
  if (retryable.length > 0) {
    const highestAttempt = Math.max(
      ...retryable.map((event) => analyticsBatchRetryCounts.get(event.idempotency_key) ?? 1)
    );
    scheduleAnalyticsMirrorFlush(ANALYTICS_BATCH_FLUSH_MS * 2 ** (highestAttempt - 1));
  }
}

function stringProp(value: CapturePrimitive) {
  return typeof value === 'string' ? value : '';
}

function webLandingPageName(pathname: string) {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/') {
    return 'home';
  }
  if (normalizedPath === '/download') {
    return 'download';
  }
  return null;
}

function webLandingPlatform() {
  const userAgent = globalThis.navigator?.userAgent?.toLowerCase() ?? '';
  if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
    return 'iOS';
  }
  if (userAgent.includes('android')) {
    return 'Android';
  }
  return 'Desktop';
}

function webSearchParam(key: string) {
  try {
    return new URL(globalThis.location?.href ?? '').searchParams.get(key)?.trim() || null;
  } catch {
    return null;
  }
}

function capture(name: string, properties: AnalyticsProps = {}, _venueID?: string | null) {
  const normalizedProperties = normalizeProps(properties);
  mirrorCapture(name, normalizedProperties);
}

async function captureAndFlush(
  name: string,
  properties: AnalyticsProps = {},
  venueID?: string | null
) {
  capture(name, properties, venueID);
  await flushPostHog();
}

async function flush() {
  const posthog = getClient();
  if (!posthog) {
    return;
  }

  try {
    await posthog.flush();
  } catch {
    return;
  }
}

function trackScreenLoad(screen: LoadScreen, loadTimeMs: number) {
  if (loadTimeMs <= 3000) {
    return;
  }
  capture('slow_load_detected', { screen, load_time_ms: Math.round(loadTimeMs) });
}

export function initPostHog() {
  void restoreAnalyticsMirrorQueue().then(() => flushAnalyticsMirror());
  if (Platform.OS === 'web') {
    if (initialized) {
      return;
    }
    initialized = true;
    void getWebDistinctID();
    globalThis.addEventListener?.('pagehide', () => {
      void flushPostHogInBackground();
    });
    return;
  }

  const posthog = getClient();
  if (!posthog || initialized) {
    return;
  }

  initialized = true;
  void ensureNativeAnalyticsIdentity();
}

type IdentifyProps = {
  userID: string;
  email?: string | null;
  username?: string | null;
};

export function identifyPostHog({ userID, email, username }: IdentifyProps) {
  if (!userID) {
    return;
  }
  analyticsIdentifiedUserID = userID;

  const traits: Record<string, string> = {};
  if (email) traits.$email = email;
  if (username) traits.username = username;

  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (!storage) {
      return;
    }
    const anonID = storage.getItem(WEB_ANALYTICS_ANON_ID_KEY);
    if (anonID && anonID !== userID) {
      sendWebPayload({
        api_key: APP_CONFIG.posthogKey,
        event: '$identify',
        distinct_id: userID,
        properties: {
          $anon_distinct_id: anonID,
          surface: 'web',
          ...traits,
        },
      });
    }
    storage.setItem(WEB_ANALYTICS_IDENTIFIED_ID_KEY, userID);
    return;
  }

  const posthog = getClient();
  if (!posthog) {
    return;
  }

  void ensureNativeAnalyticsIdentity()?.then(() => posthog.identify(userID, traits));
}

export function setPostHogFeatureFlagTraits(traits: FeatureFlagTraits) {
  const posthog = getClient();
  if (!posthog) {
    return;
  }

  const properties: Record<string, string | number | boolean> = {};

  if (traits.appVersion) {
    properties.app_version = traits.appVersion;
  }
  if (typeof traits.isStaff === 'boolean') {
    properties.is_staff = traits.isStaff;
  }
  if (typeof traits.hasClaimedVenue === 'boolean') {
    properties.has_claimed_venue = traits.hasClaimedVenue;
  }
  if (typeof traits.orgMembershipCount === 'number') {
    properties.org_membership_count = traits.orgMembershipCount;
  }
  if (typeof traits.hasBusinessContext === 'boolean') {
    properties.has_business_context = traits.hasBusinessContext;
  }
  if (traits.platform) {
    properties.platform = traits.platform;
  }

  if (Object.keys(properties).length === 0) {
    return;
  }

  posthog.setPersonPropertiesForFlags(properties, true);
}

export function getPostHogFeatureFlag(key: string) {
  const posthog = getClient();
  if (!posthog) {
    return undefined;
  }

  return posthog.getFeatureFlag(key);
}

export function reloadPostHogFeatureFlags() {
  const posthog = getClient();
  if (!posthog) {
    return;
  }

  posthog.reloadFeatureFlags();
}

export function subscribeToPostHogFeatureFlags(callback: () => void) {
  const posthog = getClient();
  if (!posthog) {
    return () => undefined;
  }

  return posthog.onFeatureFlags(() => {
    callback();
  });
}

export function setAnalyticsMirrorAccessToken(accessToken?: string | null) {
  analyticsMirrorAccessToken = accessToken ?? null;
  if (analyticsMirrorAccessToken) {
    void flushAnalyticsMirror();
  }
}

export function resetPostHog() {
  analyticsIdentifiedUserID = null;
  analyticsSessionID = createAnalyticsID();
  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    analyticsMirrorAccessToken = null;
    lastWebPageViewKey = null;
    if (!storage) {
      return;
    }
    storage.removeItem(WEB_ANALYTICS_IDENTIFIED_ID_KEY);
    storage.setItem(WEB_ANALYTICS_ANON_ID_KEY, createAnalyticsID());
    return;
  }

  const posthog = getClient();
  if (!posthog) {
    analyticsAnonymousID = createAnalyticsID();
    nativeAnalyticsIdentityPromise = AsyncStorage.setItem(
      NATIVE_ANALYTICS_ANON_ID_KEY,
      analyticsAnonymousID
    ).catch(() => undefined);
    analyticsMirrorAccessToken = null;
    return;
  }

  analyticsMirrorAccessToken = null;
  nativeAnalyticsIdentityPromise = Promise.resolve(posthog.reset())
    .then(() => posthog.getDistinctId())
    .then((distinctID) => {
      analyticsAnonymousID = distinctID || createAnalyticsID();
      return AsyncStorage.setItem(NATIVE_ANALYTICS_ANON_ID_KEY, analyticsAnonymousID);
    })
    .catch(() => undefined);
}

export async function flushPostHog() {
  await flushAnalyticsMirror();
  await flush();
}

export async function flushPostHogInBackground(timeoutMs = 1000) {
  await Promise.race([
    flushPostHog(),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

export function trackWebPageView(pathname: string) {
  if (Platform.OS !== 'web') {
    return;
  }

  const currentURL = redactAnalyticsURL(globalThis.location?.href, pathname);
  const key = `${pathname}|${currentURL}`;
  if (lastWebPageViewKey === key) {
    return;
  }

  lastWebPageViewKey = key;
  capture('$pageview', {
    pathname,
    $current_url: currentURL,
  });

  const pageName = webLandingPageName(pathname);
  if (!pageName) {
    return;
  }

  const utmSource = webSearchParam('utm_source');
  const utmMedium = webSearchParam('utm_medium');
  const utmCampaign = webSearchParam('utm_campaign');
  const qrSlug = webSearchParam('qr_slug');
  capture('landing_page_view', {
    page_name: pageName,
    page_path: pathname,
    platform: webLandingPlatform(),
    referrer: globalThis.document?.referrer
      ? redactAnalyticsURL(globalThis.document.referrer, '/')
      : null,
    source: utmSource,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    qr_slug: qrSlug,
    surface: 'landing_site',
  });
}

export function trackLaunchEvent(name: LaunchEventName, properties: AnalyticsProps = {}) {
  capture(name, properties);
}

export function trackLandingCTAClicked({
  label,
  destination,
  href,
  pageName,
}: {
  label: string;
  destination: string;
  href: string;
  pageName?: string | null;
}) {
  if (Platform.OS !== 'web') {
    return;
  }

  const pathname = globalThis.location?.pathname ?? '';
  const utmSource = webSearchParam('utm_source');
  const utmMedium = webSearchParam('utm_medium');
  const utmCampaign = webSearchParam('utm_campaign');
  const qrSlug = webSearchParam('qr_slug');
  capture('landing_cta_clicked', {
    cta_label: label,
    destination,
    href,
    page_name: pageName ?? webLandingPageName(pathname),
    page_path: pathname,
    source: utmSource,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    qr_slug: qrSlug,
    surface: 'landing_site',
  });
}

export function trackAppOpen({
  isAuthenticated,
  route,
  userCounty,
}: {
  isAuthenticated: boolean;
  route?: string | null;
  userCounty?: string | null;
}) {
  capture('app_open', {
    is_authenticated: isAuthenticated,
    route: route ? normalizedScreenPath(route) : null,
    user_county: userCounty ?? null,
  });
}

function normalizedScreenPath(pathname: string) {
  return (
    pathname
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, '[id]')
      .replace(/\/event\/[^/]+/g, '/event/[id]')
      .replace(/\/venues\/[^/]+/g, '/venues/[id]')
      .replace(/\/profiles\/[^/]+/g, '/profiles/[username]')
      .replace(/\/workspaces\/[^/]+/g, '/workspaces/[id]') || '/'
  );
}

export function trackScreenView({
  isAuthenticated,
  pathname,
  rootSegment,
  tabSegment,
}: {
  isAuthenticated: boolean;
  pathname: string;
  rootSegment?: string | null;
  tabSegment?: string | null;
}) {
  if (Platform.OS === 'web') {
    return;
  }

  const screen = normalizedScreenPath(pathname);
  capture('screen_view', {
    is_authenticated: isAuthenticated,
    root_segment: rootSegment ?? null,
    screen,
    surface: 'mobile',
    tab_segment: tabSegment ?? null,
  });
}

export function trackEmptyStateSeen({
  reason,
  screen,
  userCounty,
}: {
  reason: EmptyStateReason;
  screen: string;
  userCounty?: string | null;
}) {
  capture('empty_state_seen', {
    screen,
    user_county: userCounty ?? null,
    reason,
  });
}

export function trackSearchQuery({
  query,
  resultsCount,
  searchCorrelationID,
  userCounty,
  sessionKey,
}: {
  query: string;
  resultsCount: number;
  searchCorrelationID: string;
  userCounty?: string | null;
  sessionKey: string;
}) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return;
  }

  const safeSearch = deriveSafeSearchAnalytics(query, searchCorrelationID);
  const dedupeKey = `${sessionKey}:${safeSearch.search_correlation_id}`;
  if (searchQueryKeys.has(dedupeKey)) {
    return;
  }

  searchQueryKeys.add(dedupeKey);
  capture('search_query', {
    ...safeSearch,
    results_count: resultsCount,
    source: 'search',
    surface: searchSurface(),
    user_county: userCounty ?? null,
  });
}

export function trackSearchResultTapped({
  dealID,
  eventID,
  position,
  query,
  searchCorrelationID,
  targetType,
  venueID,
}: {
  dealID?: string | null;
  eventID?: string | null;
  position: number;
  query: string;
  searchCorrelationID: string;
  targetType: 'deal' | 'event' | 'profile' | 'venue';
  venueID?: string | null;
}) {
  capture(
    'search_result_tapped',
    {
      ...deriveSafeSearchAnalytics(query, searchCorrelationID),
      position,
      target_type: targetType,
      deal_id: dealID ?? null,
      event_id: eventID ?? null,
      source: 'search',
      surface: searchSurface(),
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackMapOpened(userCounty?: string | null, surface: AnalyticsSurface = 'mobile') {
  capture('map_opened', { source: 'map', surface, user_county: userCounty ?? null });
}

export function trackMapVenueTap({
  venueID,
  venueName,
  surface = 'mobile_map_sheet',
  userCounty,
}: {
  venueID: string;
  venueName: string;
  surface?: AnalyticsSurface;
  userCounty?: string | null;
}) {
  capture(
    'map_venue_tap',
    {
      venue_id: venueID,
      venue_name: venueName,
      source: 'map',
      surface,
      user_county: userCounty ?? null,
    },
    venueID
  );
}

export function trackMapVenuePinTapped(venueID: string, surface: AnalyticsSurface = 'mobile') {
  capture(
    'map_venue_pin_tapped',
    {
      venue_id: venueID,
      source: 'map',
      surface,
    },
    venueID
  );
}

export function trackVenueMapAppearance({
  position,
  sessionKey,
  surface = 'mobile',
  venueID,
}: {
  position: number;
  sessionKey: string;
  surface?: AnalyticsSurface;
  venueID: string;
}) {
  capture(
    'venue_map_appearance',
    {
      idempotency_key: `map:${sessionKey}:venue:${venueID}`,
      position,
      source: 'map',
      surface,
      venue_id: venueID,
    },
    venueID
  );
}

export function trackMapEventPinTapped({
  eventID,
  surface = 'mobile',
  venueID,
}: {
  eventID: string;
  surface?: AnalyticsSurface;
  venueID?: string | null;
}) {
  capture(
    'map_event_pin_tapped',
    {
      event_id: eventID,
      source: 'map',
      surface,
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackMapFilterApplied(
  filterType: MapFilterType,
  filterValue: string,
  surface: AnalyticsSurface = 'mobile'
) {
  capture('map_filter_applied', {
    filter_type: filterType,
    filter_value: filterValue,
    source: 'map',
    surface,
  });
}

export function trackFeedFilterApplied(
  filterType: 'time_window' | 'radius' | 'mode',
  filterValue: string
) {
  capture('feed_filter_applied', { filter_type: filterType, filter_value: filterValue });
}

export function trackFeedRefreshed(eventsCount: number, userCounty?: string | null) {
  capture('feed_refreshed', {
    events_count: eventsCount,
    user_county: userCounty ?? null,
  });
}

export function trackCategoryFilterApplied(category: string) {
  capture('category_filter_applied', { category });
}

export function trackEventView({
  distanceFromUser,
  eventDate,
  eventID,
  eventName,
  eventType,
  feedPosition,
  qrSlug,
  source,
  surface,
  utmCampaign,
  utmMedium,
  utmSource,
  userCounty,
  venueID,
  venueName,
}: {
  distanceFromUser?: number | null;
  eventDate: string;
  eventID: string;
  eventName: string;
  eventType: string;
  feedPosition?: number | null;
  qrSlug?: string | null;
  source?: AnalyticsSource;
  surface?: AnalyticsSurface;
  utmCampaign?: string | null;
  utmMedium?: string | null;
  utmSource?: string | null;
  userCounty?: string | null;
  venueID?: string | null;
  venueName: string;
}) {
  capture(
    'event_view',
    {
      event_id: eventID,
      event_name: eventName,
      venue_id: venueID ?? null,
      venue_name: venueName,
      user_county: userCounty ?? null,
      distance_from_user: distanceFromUser ?? null,
      event_date: eventDate,
      event_type: eventType,
      feed_position: feedPosition ?? null,
      qr_slug: qrSlug ?? null,
      source: source ?? (feedPosition == null ? 'event_detail' : 'feed'),
      surface: surface ?? 'mobile_event_detail',
      utm_campaign: utmCampaign ?? null,
      utm_medium: utmMedium ?? null,
      utm_source: utmSource ?? null,
    },
    venueID
  );
}

export function trackEventFeedImpression({
  eventID,
  position,
  sessionKey,
  contextKey,
  surface = 'mobile_feed',
  venueID,
}: {
  eventID: string;
  position: number;
  sessionKey: string;
  contextKey: string;
  surface?: AnalyticsSurface;
  venueID?: string | null;
}) {
  capture(
    'event_impression',
    {
      event_id: eventID,
      idempotency_key: `feed:${sessionKey}:${analyticsQueryKey(contextKey)}:event:${eventID}`,
      position,
      source: 'feed',
      surface,
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackEventShare({
  eventID,
  source = 'event_detail',
  shareMethod,
  surface = 'mobile_event_detail',
  venueID,
}: {
  eventID: string;
  source?: AnalyticsSource;
  shareMethod: EventShareMethod;
  surface?: AnalyticsSurface;
  venueID?: string | null;
}) {
  void captureAndFlush(
    'event_share',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
      share_method: shareMethod,
      source,
      surface,
    },
    venueID
  );
}

export function trackEventLinkTapped({
  eventID,
  linkType,
  source = 'event_detail',
  surface = 'mobile_event_detail',
  venueID,
}: {
  eventID: string;
  linkType: EventLinkType;
  source?: AnalyticsSource;
  surface?: AnalyticsSurface;
  venueID?: string | null;
}) {
  capture(
    'event_link_tapped',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
      link_type: linkType,
      source,
      surface,
    },
    venueID
  );
}

export async function trackCalendarExportTapped({
  eventID,
  venueID,
}: {
  eventID: string;
  venueID?: string | null;
}) {
  await captureAndFlush(
    'calendar_export_tapped',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
      source: 'event_detail',
      surface: 'mobile_event_detail',
    },
    venueID
  );
}

export async function trackTicketLinkTapped({
  eventID,
  isPaid,
  source = 'event_detail',
  surface = 'mobile_event_detail',
  venueID,
}: {
  eventID: string;
  isPaid: boolean;
  source?: AnalyticsSource;
  surface?: AnalyticsSurface;
  venueID?: string | null;
}) {
  await captureAndFlush(
    'ticket_link_tapped',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
      is_paid: isPaid,
      source,
      surface,
    },
    venueID
  );
}

export function trackEventImageViewed(eventID: string, venueID?: string | null) {
  capture(
    'event_image_viewed',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackEventViewDuration({
  eventID,
  secondsOnScreen,
  source = 'event_detail',
  surface = 'mobile_event_detail',
  venueID,
}: {
  eventID: string;
  secondsOnScreen: number;
  source?: AnalyticsSource;
  surface?: AnalyticsSurface;
  venueID?: string | null;
}) {
  if (secondsOnScreen < VIEW_DURATION_THRESHOLD_SECONDS) {
    return;
  }

  capture(
    'event_view_duration',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
      seconds_on_screen: secondsOnScreen,
      source,
      surface,
    },
    venueID
  );
}

export function trackVenueProfileView({
  surface,
  source,
  userCounty,
  venueID,
  venueName,
  profileUserID,
  profileUsername,
}: {
  surface?: AnalyticsSurface;
  source: VenueProfileSource;
  userCounty?: string | null;
  venueID?: string | null;
  venueName: string;
} & VenueProfileIdentity) {
  capture(
    'venue_profile_view',
    {
      venue_id: venueID ?? null,
      venue_name: venueName,
      user_county: userCounty ?? null,
      profile_user_id: profileUserID ?? null,
      profile_username: profileUsername ?? null,
      source,
      surface: surface ?? venueProfileSurface(source),
    },
    venueID
  );
}

function venueProfileSurface(source: VenueProfileSource): AnalyticsSurface {
  if (source === 'map_sheet') return 'mobile_map_sheet';
  if (source === 'search') return 'mobile_search';
  if (source === 'event_detail') return 'mobile_event_detail';
  return 'mobile_venue_page';
}

export function trackVenueOwnerProfileViewed({
  userCounty,
  venueID,
  venueName,
  profileUserID,
  profileUsername,
}: {
  userCounty?: string | null;
  venueID?: string | null;
  venueName: string;
} & VenueProfileIdentity) {
  capture(
    'venue_owner_profile_view',
    {
      venue_id: venueID ?? null,
      venue_name: venueName,
      user_county: userCounty ?? null,
      profile_user_id: profileUserID ?? null,
      profile_username: profileUsername ?? null,
      source: 'profile',
      surface: 'mobile',
    },
    venueID
  );
}

export function trackVenueFollow(
  venueName: string,
  venueID?: string | null,
  identity: VenueProfileIdentity = {}
) {
  void captureAndFlush(
    'venue_follow',
    {
      venue_id: venueID ?? null,
      venue_name: venueName,
      profile_user_id: identity.profileUserID ?? null,
      profile_username: identity.profileUsername ?? null,
      source: 'venue_page',
      surface: identity.surface ?? 'mobile_venue_page',
    },
    venueID
  );
}

export function trackVenueUnfollow(
  venueName: string,
  venueID?: string | null,
  identity: VenueProfileIdentity = {}
) {
  void captureAndFlush(
    'venue_unfollow',
    {
      venue_id: venueID ?? null,
      venue_name: venueName,
      profile_user_id: identity.profileUserID ?? null,
      profile_username: identity.profileUsername ?? null,
      source: 'venue_page',
      surface: identity.surface ?? 'mobile_venue_page',
    },
    venueID
  );
}

export function trackVenueWebsiteTapped({
  venueID,
  venueName,
  surface,
}: {
  venueID: string;
  venueName: string;
  surface: VenueLinkSurface;
}) {
  capture(
    'venue_website_tapped',
    {
      venue_id: venueID,
      venue_name: venueName,
      source: venueLinkSource(surface),
      surface: venueLinkSurface(surface),
    },
    venueID
  );
}

export function trackVenueBookingTapped({
  surface = 'mobile_venue_page',
  venueID,
  venueName,
}: {
  surface?: AnalyticsSurface;
  venueID: string;
  venueName: string;
}) {
  capture(
    'venue_booking_tapped',
    {
      venue_id: venueID,
      venue_name: venueName,
      source: 'venue_page',
      surface,
    },
    venueID
  );
}

export function trackVenueInstagramTapped({
  venueID,
  venueName,
  surface,
}: {
  venueID: string;
  venueName: string;
  surface: VenueLinkSurface;
}) {
  capture(
    'venue_instagram_tapped',
    {
      venue_id: venueID,
      venue_name: venueName,
      source: venueLinkSource(surface),
      surface: venueLinkSurface(surface),
    },
    venueID
  );
}

export function trackVenueSocialLinkTapped({
  platform,
  surface = 'mobile_venue_page',
  venueID,
  venueName,
}: {
  platform: string;
  surface?: AnalyticsSurface;
  venueID: string;
  venueName: string;
}) {
  capture(
    'venue_social_link_tapped',
    {
      platform,
      venue_id: venueID,
      venue_name: venueName,
      source: 'venue_page',
      surface,
    },
    venueID
  );
}

export function trackVenueDirectionsTapped({
  surface,
  venueID,
  venueName,
  userCounty,
}: {
  surface: VenueDirectionsSurface;
  venueID: string;
  venueName: string;
  userCounty?: string | null;
}) {
  void captureAndFlush(
    'venue_directions_tapped',
    {
      venue_id: venueID,
      venue_name: venueName,
      source: venueLinkSource(surface),
      surface: venueLinkSurface(surface),
      user_county: userCounty ?? null,
    },
    venueID
  );
}

function venueLinkSurface(surface: VenueDirectionsSurface | VenueLinkSurface): AnalyticsSurface {
  if (surface === 'web_map') {
    return 'web';
  }
  if (surface === 'web_venue_page') {
    return 'web';
  }
  return surface === 'map_sheet' ? 'mobile_map_sheet' : 'mobile_venue_page';
}

function venueLinkSource(surface: VenueDirectionsSurface | VenueLinkSurface): AnalyticsSource {
  return surface === 'map_sheet' || surface === 'web_map' ? 'map' : 'venue_page';
}

function analyticsQueryKey(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, '-').slice(0, 48) || 'context';
}

function searchSurface(): AnalyticsSurface {
  return Platform.OS === 'web' ? 'web' : 'mobile_search';
}

export function trackDealImpression({
  category,
  dealID,
  position,
  source,
  surface,
  timingState,
  venueID,
}: {
  category?: string | null;
  dealID: string;
  position?: number | null;
  source: AnalyticsSource;
  surface: AnalyticsSurface;
  timingState?: string | null;
  venueID: string;
}) {
  capture(
    'deal_impression',
    {
      category: category ?? null,
      deal_id: dealID,
      position: position ?? null,
      source,
      surface,
      timing_state: timingState ?? null,
      venue_id: venueID,
    },
    venueID
  );
}

export function trackEventSearchAppearance({
  eventID,
  position,
  query,
  searchCorrelationID,
  sessionKey,
  venueID,
}: {
  eventID: string;
  position: number;
  query: string;
  searchCorrelationID: string;
  sessionKey: string;
  venueID?: string | null;
}) {
  const safeSearch = deriveSafeSearchAnalytics(query, searchCorrelationID);
  capture(
    'event_search_appearance',
    {
      event_id: eventID,
      ...safeSearch,
      idempotency_key: `search:${sessionKey}:${safeSearch.search_correlation_id}:event:${eventID}:${position}`,
      position,
      source: 'search',
      surface: searchSurface(),
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackDealSearchAppearance({
  dealID,
  position,
  query,
  searchCorrelationID,
  sessionKey,
  venueID,
}: {
  dealID: string;
  position: number;
  query: string;
  searchCorrelationID: string;
  sessionKey: string;
  venueID: string;
}) {
  const safeSearch = deriveSafeSearchAnalytics(query, searchCorrelationID);
  capture(
    'deal_search_appearance',
    {
      deal_id: dealID,
      ...safeSearch,
      idempotency_key: `search:${sessionKey}:${safeSearch.search_correlation_id}:deal:${dealID}:${position}`,
      position,
      source: 'search',
      surface: searchSurface(),
      venue_id: venueID,
    },
    venueID
  );
}

export function trackVenueSearchAppearance({
  position,
  query,
  searchCorrelationID,
  sessionKey,
  venueID,
}: {
  position: number;
  query: string;
  searchCorrelationID: string;
  sessionKey: string;
  venueID: string;
}) {
  const safeSearch = deriveSafeSearchAnalytics(query, searchCorrelationID);
  capture(
    'venue_search_appearance',
    {
      ...safeSearch,
      idempotency_key: `search:${sessionKey}:${safeSearch.search_correlation_id}:venue:${venueID}:${position}`,
      position,
      source: 'search',
      surface: searchSurface(),
      venue_id: venueID,
    },
    venueID
  );
}

export function trackDealTap({
  category,
  dealID,
  position,
  source,
  surface,
  timingState,
  venueID,
}: {
  category?: string | null;
  dealID: string;
  position?: number | null;
  source: AnalyticsSource;
  surface: AnalyticsSurface;
  timingState?: string | null;
  venueID: string;
}) {
  capture(
    'deal_tap',
    {
      category: category ?? null,
      deal_id: dealID,
      position: position ?? null,
      source,
      surface,
      timing_state: timingState ?? null,
      venue_id: venueID,
    },
    venueID
  );
}

export function trackDealSaved({
  dealID,
  isSaved,
  source,
  surface,
  venueID,
}: {
  dealID: string;
  isSaved: boolean;
  source: AnalyticsSource;
  surface: AnalyticsSurface;
  venueID: string;
}) {
  void captureAndFlush(
    isSaved ? 'deal_saved' : 'deal_unsaved',
    {
      deal_id: dealID,
      source,
      surface,
      venue_id: venueID,
    },
    venueID
  );
}

export function trackDealShare({
  dealID,
  source,
  surface,
  venueID,
}: {
  dealID: string;
  source: AnalyticsSource;
  surface: AnalyticsSurface;
  venueID: string;
}) {
  void captureAndFlush(
    'deal_share',
    {
      deal_id: dealID,
      source,
      surface,
      venue_id: venueID,
    },
    venueID
  );
}

export function trackDealDirectionsTapped({
  dealID,
  source,
  surface,
  venueID,
}: {
  dealID: string;
  source: AnalyticsSource;
  surface: AnalyticsSurface;
  venueID: string;
}) {
  void captureAndFlush(
    'deal_directions_tapped',
    {
      deal_id: dealID,
      source,
      surface,
      venue_id: venueID,
    },
    venueID
  );
}

export function trackVenueShare(
  venueName: string,
  venueID?: string | null,
  identity: VenueProfileIdentity = {}
) {
  void captureAndFlush(
    'venue_share',
    {
      venue_id: venueID ?? null,
      venue_name: venueName,
      profile_user_id: identity.profileUserID ?? null,
      profile_username: identity.profileUsername ?? null,
      source: 'venue_page',
      surface: identity.surface ?? 'mobile_venue_page',
    },
    venueID
  );
}

export function trackVenueClaimStarted({
  source,
  venueID,
  venueName,
}: {
  source: VenueClaimSource;
  venueID: string;
  venueName: string;
}) {
  capture(
    'venue_claim_started',
    {
      source,
      venue_id: venueID,
      venue_name: venueName,
    },
    venueID
  );
}

export function trackOnboardingStarted() {
  capture('onboarding_started');
}

export function trackOnboardingCompleted(stepsCompleted: number, timeToCompleteSeconds: number) {
  capture('onboarding_completed', {
    steps_completed: stepsCompleted,
    time_to_complete_seconds: timeToCompleteSeconds,
  });
}

export function trackOnboardingAbandoned(stepAbandoned: OnboardingStep) {
  capture('onboarding_abandoned', { step_abandoned: stepAbandoned });
}

export function trackAccountCreated(method: 'email' | 'apple' | 'google') {
  void captureAndFlush('account_created', { method });
}

export function trackLogin(method: 'email' | 'apple' | 'google') {
  void captureAndFlush('login', { method });
}

export function trackLogout() {
  void captureAndFlush('logout');
}

export function trackLocationPermission({
  granted,
  source,
}: {
  granted: boolean;
  source: PermissionPromptSource;
}) {
  capture(granted ? 'location_permission_granted' : 'location_permission_denied', {
    source,
  });
}

export function trackNotificationPermission({
  granted,
  source,
}: {
  granted: boolean;
  source: PermissionPromptSource;
}) {
  capture(granted ? 'notification_permission_granted' : 'notification_permission_denied', {
    source,
  });
}

export function trackProfileUpdated(fieldsChanged: ProfileField[]) {
  if (fieldsChanged.length === 0) {
    return;
  }
  capture('profile_updated', { fields_changed: fieldsChanged.join(',') });
}

export function trackEventPublished(eventID: string, venueID?: string | null) {
  capture('event_published', { event_id: eventID, venue_id: venueID ?? null }, venueID);
}

export function trackEventEdited({
  eventID,
  fieldsChanged,
  venueID,
}: {
  eventID: string;
  fieldsChanged: string[];
  venueID?: string | null;
}) {
  if (fieldsChanged.length === 0) {
    return;
  }
  capture(
    'event_edited',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
      fields_changed: fieldsChanged.join(','),
    },
    venueID
  );
}

export function trackEventSaved({
  eventID,
  venueID,
  venueName,
  surface,
}: {
  eventID: string;
  venueID?: string | null;
  venueName: string;
  surface: 'feed' | 'detail' | 'web';
}) {
  const analyticsSource = surface === 'detail' ? 'event_detail' : 'feed';
  void captureAndFlush(
    'event_saved',
    {
      event_id: eventID,
      source: analyticsSource,
      surface:
        surface === 'detail' ? 'mobile_event_detail' : surface === 'web' ? 'web' : 'mobile_feed',
      venue_id: venueID ?? null,
      venue_name: venueName || null,
    },
    venueID
  );
}

export function trackEventUnsaved({
  eventID,
  venueID,
  venueName,
  surface,
}: {
  eventID: string;
  venueID?: string | null;
  venueName: string;
  surface: 'feed' | 'detail' | 'web';
}) {
  const analyticsSource = surface === 'detail' ? 'event_detail' : 'feed';
  capture(
    'event_unsaved',
    {
      event_id: eventID,
      source: analyticsSource,
      surface:
        surface === 'detail' ? 'mobile_event_detail' : surface === 'web' ? 'web' : 'mobile_feed',
      venue_id: venueID ?? null,
      venue_name: venueName || null,
    },
    venueID
  );
}

export function trackEventNotInterested({
  eventID,
  position,
  reason,
  venueID,
}: {
  eventID: string;
  position: number;
  reason: string;
  venueID?: string | null;
}) {
  capture(
    'event_not_interested',
    {
      event_id: eventID,
      position,
      reason,
      source: 'feed',
      surface: 'mobile_feed',
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackPushNotificationOpened({
  eventID,
  notificationID,
  notificationType,
  organizationID,
  venueID,
}: {
  eventID?: string | null;
  notificationID?: string | null;
  notificationType: string;
  organizationID?: string | null;
  venueID?: string | null;
}) {
  void captureAndFlush('push_notification_opened', {
    event_id: eventID ?? null,
    notification_id: notificationID ?? null,
    notification_type: notificationType,
    organization_id: organizationID ?? null,
    source: 'push',
    surface: 'mobile',
    venue_id: venueID ?? null,
  });
}

export function trackSignUpStarted(method: 'email' | 'apple' | 'google') {
  capture('sign_up_started', { method });
}

export function trackSignUpStepCompleted(step: 'credentials' | 'profile') {
  capture('sign_up_step_completed', { step });
}

export function trackGuestWritePromptShown(action: string) {
  capture('guest_write_prompt_shown', { attempted_action: action });
}

export function trackTabSwitched(tab: string) {
  capture('tab_switched', { tab });
}

export function trackWorkspaceContextSwitched({
  organizationID,
  targetContext,
  venueID,
}: {
  organizationID?: string | null;
  targetContext: WorkspaceContextKind;
  venueID?: string | null;
}) {
  capture(
    'workspace_context_switched',
    {
      organization_id: organizationID ?? null,
      source: 'account_context_sheet',
      surface: 'mobile_business',
      target_context: targetContext,
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackBusinessActionTapped({
  action,
  eventID,
  organizationID,
  source,
  venueID,
}: {
  action: BusinessAction;
  eventID?: string | null;
  organizationID?: string | null;
  source: string;
  venueID?: string | null;
}) {
  capture(
    'business_action_tapped',
    {
      action,
      event_id: eventID ?? null,
      organization_id: organizationID ?? null,
      source,
      surface: 'mobile_business',
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackBusinessNotificationAction({
  action,
  notificationID,
  notificationType,
  organizationID,
  venueID,
}: {
  action: BusinessNotificationAction;
  notificationID?: string | null;
  notificationType?: string | null;
  organizationID?: string | null;
  venueID?: string | null;
}) {
  capture(
    'business_notification_action',
    {
      action,
      notification_id: notificationID ?? null,
      notification_type: notificationType ?? null,
      organization_id: organizationID ?? null,
      source: 'business_inbox',
      surface: 'mobile_business_inbox',
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackBusinessDealAction({
  action,
  category,
  dealID,
  venueID,
}: {
  action: BusinessDealAction;
  category?: string | null;
  dealID?: string | null;
  venueID: string;
}) {
  capture(
    'business_deal_action',
    {
      action,
      category: category ?? null,
      deal_id: dealID ?? null,
      source: 'business_deals',
      surface: 'mobile_business',
      venue_id: venueID,
    },
    venueID
  );
}

export function trackCommentSubmitted({
  eventID,
  venueID,
}: {
  eventID: string;
  venueID?: string | null;
}) {
  capture(
    'comment_submitted',
    {
      event_id: eventID,
      source: 'event_detail',
      surface: 'mobile_event_detail',
      venue_id: venueID ?? null,
    },
    venueID
  );
}

export function trackUserFollow(targetUserID: string, targetUsername: string) {
  capture('user_follow', { target_user_id: targetUserID, target_username: targetUsername });
}

export function trackUserUnfollow(targetUserID: string, targetUsername: string) {
  capture('user_unfollow', { target_user_id: targetUserID, target_username: targetUsername });
}

export function trackApiError({
  code,
  endpoint,
  failureType,
  requestID,
  statusCode,
  userAction,
}: {
  code?: string | null;
  endpoint: string;
  failureType?: 'http' | 'network' | 'timeout';
  requestID?: string | null;
  statusCode: number;
  userAction?: UserAction;
}) {
  capture('api_error', {
    code: code ?? null,
    endpoint,
    failure_type: failureType ?? 'http',
    request_id: requestID ?? null,
    status_code: statusCode,
    user_action: userAction ?? 'unknown',
  });
}

export function trackReportSubmitted({
  hasDetails,
  reason,
  targetType,
}: {
  hasDetails: boolean;
  reason: string;
  targetType: string;
}) {
  capture('report_submitted', {
    has_details: hasDetails,
    reason,
    screen: 'report-sheet',
    target_type: targetType,
  });
}

export function trackRsvpFailed({
  eventID,
  reason,
  venueID,
}: {
  eventID: string;
  reason: string;
  venueID?: string | null;
}) {
  capture(
    'rsvp_failed',
    {
      event_id: eventID,
      venue_id: venueID ?? null,
      reason,
    },
    venueID
  );
}

export function trackImageLoadFailed(context: 'event' | 'venue' | 'profile') {
  capture('image_load_failed', { context });
}

export function createLoadTimer(screen: LoadScreen) {
  const startedAt = Date.now();
  let fired = false;

  return {
    done() {
      if (fired) {
        return;
      }
      fired = true;
      trackScreenLoad(screen, Date.now() - startedAt);
    },
  };
}
